import { HStack, Stack, Text } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { useFavouritePostsPagination } from '#modules/finder/features/posts/hooks/useFavouritePostsPagination';
import {
  ProfileAgenda,
  type ProfileAgendaView,
} from '#modules/user/features/profile/components/Profile/ProfileAgenda';
import { ProfileFavoritesList } from '#modules/user/features/profile/components/Profile/ProfileFavorites';
import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import { useGames } from '#features/game';
import { useCurrentLocale } from '#features/i18n';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { Button } from '#features/ui';
import { toaster } from '#features/ui/components/Toaster';

/** Локальная приёмка: данные копируются только для отображения, API не изменяется. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/profile-favorites')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: z.object({ lang: z.enum(['ru', 'en']).default('ru') }),
  component: FavoritesPreviewRoute,
});

function FavoritesPreviewRoute() {
  const { lang } = Route.useSearch();
  return <LocalizedPreview key={lang} lang={lang} />;
}

function LocalizedPreview({ lang }: { lang: 'ru' | 'en' }) {
  // Изолированная локаль для QA без изменения языковых настроек аккаунта.
  const [i18n] = useState(() =>
    setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } })
  );
  return (
    <I18nProvider i18n={i18n}>
      <FavoritesPreview />
    </I18nProvider>
  );
}

const states = ['empty', 'loading', 'error', 'more', 'more-loading', 'more-error', 'done'] as const;
type PreviewState = (typeof states)[number];

function FavoritesPreview() {
  const { t } = useLingui();
  const locale = useCurrentLocale();
  const navigate = useNavigate();
  const [state, setState] = useState<PreviewState>('empty');
  const [view, setView] = useState<ProfileAgendaView>('favorites');
  const { profile } = useProfile();
  const list = useFavouritePostsPagination({ userId: profile?.id });
  const games = useGames();
  const longestGame = Array.isArray(games)
    ? games
        .flatMap((game) =>
          game && 'name' in game && 'id' in game ? [{ id: game.id, name: game.name }] : []
        )
        .toSorted((a, b) => b.name.length - a.name.length)[0]
    : undefined;
  const posts = list.posts.map((post, index) => ({
    ...post,
    gameId: index === 0 ? (longestGame?.id ?? post.gameId) : post.gameId,
    text: `${post.text ?? ''} Пример для проверки длинного текста: ищу команду для совместной игры по вечерам, общения и развития навыков.`,
    userData: post.userData
      ? { ...post.userData, displayName: 'LongPlayerNameДляПроверкиПереносов' }
      : post.userData,
  }));
  const showPosts = ['more', 'more-loading', 'more-error', 'done'].includes(state);
  const retry = () => setState('empty');
  const loadMore = () => {
    setState('more-loading');
    window.setTimeout(() => setState(state === 'more-error' ? 'done' : 'more-error'), 2000);
  };

  return (
    <Stack gap={6} py={6} px={4} maxW="640px" mx="auto" w="100%">
      <Text textStyle="title">DEV: Favorites states · sample content</Text>
      <HStack gap={2} flexWrap="wrap">
        <Button
          onClick={() =>
            toaster.create({
              title: t`favoriteSuccess`,
              type: 'success',
              duration: 20000,
              action: {
                label: t`Open`,
                onClick: () =>
                  void navigate({
                    to: '/{-$locale}/dashboard/profile',
                    params: { locale },
                    search: { tab: 'favorites' },
                    hash: 'profile-content',
                  }),
              },
            })
          }
        >
          toast
        </Button>
        {states.map((value) => (
          <Button
            key={value}
            onClick={() => {
              setState(value);
              setView('favorites');
            }}
            aria-pressed={state === value}
          >
            {value}
          </Button>
        ))}
      </HStack>
      <Text textStyle="label">Own profile: 0 posts, 0 sessions</Text>
      <ProfileAgenda
        isMe
        sessions={[]}
        posts={[]}
        selectedView={view}
        onViewChange={setView}
        favoritesSlot={
          <ProfileFavoritesList
            bookmarkDisabled
            posts={showPosts ? posts : []}
            isLoading={state === 'loading' || (showPosts && list.isLoading)}
            isError={state === 'error'}
            hasMore={state !== 'done'}
            isFetchingNextPage={state === 'more-loading'}
            isFetchNextPageError={state === 'more-error'}
            onRetry={retry}
            onLoadMore={loadMore}
          />
        }
      />
    </Stack>
  );
}
